import { useMemo } from "react";
import useCurrentUser from "./useCurrentUser";
import { rgbaObjectToString } from "../helpers/utils";
import moment from "moment";

interface IRgbaColor {
  r: string;
  g: string;
  b: string;
  a: string;
}

export default function useColors() {
  const { colors: userCustomColors, getAccountById } = useCurrentUser();

  const getColorWithFullOpacity = (color: IRgbaColor) => {
    return rgbaObjectToString({ ...color, a: 1 });
  };

  const colors = useMemo(() => {
    return {
      income: {
        color: getColorWithFullOpacity(
          userCustomColors.transactions.income.color,
        ),
        bg: getColorWithFullOpacity(userCustomColors.transactions.income.bg),
      },
      expenses: {
        color: getColorWithFullOpacity(
          userCustomColors.transactions.expenses.color,
        ),
        bg: getColorWithFullOpacity(userCustomColors.transactions.expenses.bg),
      },
      difference: {
        positive: {
          color: getColorWithFullOpacity(
            userCustomColors.balance.positive.color,
          ),
          bg: getColorWithFullOpacity(userCustomColors.balance.positive.bg),
        },
        negative: {
          color: getColorWithFullOpacity(
            userCustomColors.balance.negative.color,
          ),
          bg: getColorWithFullOpacity(userCustomColors.balance.negative.bg),
        },
      },
      days: userCustomColors.days,
      months: userCustomColors.months,
      statuses: userCustomColors.statuses,
      files: userCustomColors.files,
    };
  }, [userCustomColors]);

  const getAccountColors = (account: string) => {
    const accountData = getAccountById(account);

    return {
      string: {
        background: rgbaObjectToString(
          accountData?.settings?.bg || { r: 0, g: 0, b: 0, a: 1 },
        ),
        color: rgbaObjectToString(
          accountData?.settings?.color || {
            r: 0,
            g: 0,
            b: 0,
            a: 1,
          },
        ),
      },
      rgba: {
        background: accountData?.settings?.bg || { r: 0, g: 0, b: 0, a: 1 },
        color: accountData?.settings?.color || { r: 0, g: 0, b: 0, a: 1 },
      },
    };
  };

  const getDayColors = (date: string) => {
    const dayNumber = ((moment(date).day() + 6) % 7) + 1;
    const dayColors = colors.days[dayNumber];

    return {
      string: {
        background: rgbaObjectToString(dayColors.bg),
        color: rgbaObjectToString(dayColors.color),
      },
      rgba: {
        background: dayColors.bg,
        color: dayColors.color,
      },
    };
  };

  const getDayColorsByNumber = (day: number) => {
    const dayColors = colors.days[day];

    return {
      string: {
        background: rgbaObjectToString(dayColors.bg),
        color: rgbaObjectToString(dayColors.color),
      },
      rgba: {
        background: dayColors.bg,
        color: dayColors.color,
      },
    };
  };

  const allDaysColors = useMemo(() => {
    return colors.days;
  }, [colors.days]);

  const allMonthsColors = useMemo(() => {
    return colors.months;
  }, [colors.months]);

  const getIncomeColors = () => {
    return {
      string: {
        background: colors.income.bg,
        color: colors.income.color,
      },
    };
  };

  const getExpensesColors = () => {
    return {
      string: {
        background: colors.expenses.bg,
        color: colors.expenses.color,
      },
      rgba: {},
    };
  };

  const getBalanceColors = () => {
    return {
      string: {
        positive: {
          color: rgbaObjectToString(userCustomColors.balance.positive.color),
          background: rgbaObjectToString(userCustomColors.balance.positive.bg),
        },
        negative: {
          color: rgbaObjectToString(userCustomColors.balance.negative.color),
          background: rgbaObjectToString(userCustomColors.balance.negative.bg),
        },
      },
      rgba: {
        positive: {
          color: userCustomColors.balance.positive.color,
          background: userCustomColors.balance.positive.bg,
        },
        negative: {
          color: userCustomColors.balance.negative.color,
          background: userCustomColors.balance.negative.bg,
        },
      },
    };
  };

  return {
    colors,
    rgbaObjectToString,
    getAccountColors,
    getDayColors,
    getDayColorsByNumber,
    getIncomeColors,
    getExpensesColors,
    getBalanceColors,
    allDaysColors,
    allMonthsColors,
  };
}
